import type { RetryOptions } from "ky";

import type { ApiRequest } from "@/api/types";
import { httpPublic } from "@/api/httpPublic";
import { logOut } from "@/redux/actions/logout";
import store from "@/redux/store";

const dispatch = store.dispatch;

type NewIdTokenPayload = {
  id_token?: string;
  refresh_token?: string;
};

type NewIdTokenResponse = {
  AuthenticationResult: {
    AccessToken: string;
    IdToken: string;
  };
};

type AuthRequestsTyped = {
  newIdToken: ApiRequest<NewIdTokenResponse, { body?: NewIdTokenPayload }>;
};

// retry config shared by both modes below — the backend collapses all failures
// (including transient Cognito errors and Lambda cold-starts) into a single 401
// "Invalid refresh token", making it impossible to distinguish transient from
// genuine expiry by status code alone. One retry before giving up matches the
// resilience of the old axios implementation and absorbs the common case of a
// single-attempt blip without masking a genuinely expired refresh token.
const RETRY_CONFIG: RetryOptions = {
  limit: 1,
  methods: ["post"],
  statusCodes: [401, 408, 429, 500, 502, 503, 504],
};

export const authRequestsTyped: AuthRequestsTyped = {
  // Uses httpPublic (public, no auth hooks) so tokenRefresh.ts can call this
  // without creating a circular dependency through the authenticated ky instances.
  //
  // Mode is decided entirely by the caller via presence/absence of `body`, mirroring
  // the backend's own contract (.ai/auth-cookies/status.md: "works with an empty body
  // in cookie mode... body values win if sent"). The two modes are mutually exclusive
  // by construction — never both a Cookie and an Authorization header on the same
  // request (see .ai/auth-cookies/pilot-admin-profile.md for why that invariant
  // matters for the cookie-auth pilot).
  newIdToken: function ({ body } = {}) {
    if (body) {
      // header mode — explicit token values, Authorization header, no cookie involvement.
      // Missing id_token here (e.g. localStorage cleared by a logout in another tab while
      // this request was in flight) means there is nothing to refresh with — force logout
      // immediately rather than let the original 403 propagate with no user feedback
      // (401/403 are excluded from the generic error toast, see errorToastHook).
      if (!body.id_token) {
        dispatch(logOut());
        throw new Error("Missing id_token for header-mode refresh");
      }

      return httpPublic
        .post("v1/auth/new_id_token", {
          headers: { Authorization: body.id_token },
          json: body,
          retry: RETRY_CONFIG,
        })
        .json<NewIdTokenResponse>();
    }

    // cookie mode — empty body, relies entirely on the id_token + refresh_token cookies
    return httpPublic
      .post("v1/auth/new_id_token", { credentials: "include", retry: RETRY_CONFIG })
      .json<NewIdTokenResponse>();
  },
};
